var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import * as React from 'react';
import { Route, Switch } from 'wouter';
import { AccessTokenContext } from '~/AccessToken';
import { AccessTokenStorage, Handler, PigeonHandler } from '~/Api';
import { FetchClient } from '~/ApiClient';
import { Context as ApiContext } from '~/ApiHooks';
import { ErrorHandler as AppErrorHandler } from '~/Application/ErrorHandler';
import { FlashMessage } from '~/Application/FlashMessage';
import { MenuItem, Navigation } from '~/Application/Navigation';
import * as routes from '~/Application/routes';
import { Store } from '~/Application/Store';
import { ErrorHandler } from '~/ErrorHandler';
import { FlashMessageProvider } from '~/FlashMessage';
import { Initial, Success } from '~/Result';
import * as Router from '~/Router';
import { ErrorAlert } from '~/UI';
var RootRouter = function () { return (<Switch>
		<Route path={routes.DashboardRoute.path}>
			<Navigation activeMenuItem={MenuItem.dashboard}/>
			<routes.DashboardRoute.Target />
		</Route>

		<Route path={routes.InfoCardRoute.path}>
			{function (_a) {
        var id = _a.id;
        return (<>
					<Navigation activeMenuItem={MenuItem.dashboard}/>
					<routes.DashboardRoute.Target infoCardId={id}/>
				</>);
    }}
		</Route>

		<Route path={routes.ReportRoute.path}>
			{function (_a) {
        var id = _a.id;
        return (<>
					<Navigation activeMenuItem={MenuItem.reports}/>
					<routes.ReportRoute.Target id={id}/>
				</>);
    }}
		</Route>

		<Route path={routes.ReportsRoute.path}>
			<Navigation activeMenuItem={MenuItem.reports}/>
			<routes.ReportsRoute.Target />
		</Route>

		<Route path={routes.UserProfileRoute.path}>
			<Navigation />
			<routes.UserProfileRoute.Target />
		</Route>

		<Route path={routes.PasswordSetupRoute.path}>
			<Navigation />
			<routes.PasswordSetupRoute.Target title='Nastavení hesla'/>
		</Route>

		<Route path={routes.AccountConfirmationRoute.path}>
			<Navigation />
			<routes.AccountConfirmationRoute.Target />
		</Route>

		<Route path={routes.GdprConsentRoute.path}>
			<routes.GdprConsentRoute.Target />
		</Route>

		<Route path={routes.ImpersonateRoute.path}>
			<Navigation />
			<routes.ImpersonateRoute.Target />
		</Route>

		<Route path={routes.LoginRoute.path}>
			<Navigation />
			<routes.LoginRoute.Target />
		</Route>

		<Route path={routes.PasswordResetRoute.path}>
			<Navigation />
			<routes.PasswordResetRoute.Target />
		</Route>

		<Route path={routes.NotificationsFeedRoute.path}>
			<Navigation activeMenuItem={MenuItem.notifications}/>
			<routes.NotificationsFeedRoute.Target />
		</Route>

		<Route path={routes.NotificationEmailDetailRoute.path}>
			{function (_a) {
        var notificationId = _a.notificationId, emailId = _a.emailId;
        return (<>
					<routes.NotificationEmailDetailRoute.Target emailId={emailId} notificationId={notificationId}/>
				</>);
    }}
		</Route>

		<Route path={routes.NotFoundRoute.path}>
			<Navigation />
			<routes.NotFoundRoute.Target />
		</Route>
	</Switch>); };
RootRouter.displayName = 'Application.RootRouter';
export var Application = function (_a) {
    var config = _a.config, errorLogger = _a.errorLogger, createApiClient = _a.createApiClient, pigeonAuthProvider = _a.pigeonAuthProvider, pigeonBaseUrl = _a.pigeonBaseUrl;
    var accessTokenStorage = React.useMemo(function () { return new AccessTokenStorage('mit-access-token', { expires: 1 }); }, []);
    var accessTokenState = React.useState({
        accessToken: new Initial(),
        needsFetch: React.useRef(true),
        onChange: function (token) {
            var _a;
            accessTokenStorage.save((_a = token === null || token === void 0 ? void 0 : token.id) !== null && _a !== void 0 ? _a : null);
        },
        token: accessTokenStorage.load(),
    });
    var errorHandler = React.useMemo(function () {
        var errorHandler = new AppErrorHandler(errorLogger, accessTokenState[0].accessToken.value);
        errorHandler.onAuthenticationFailure = function () {
            var setState = accessTokenState[1];
            accessTokenStorage.save(null);
            setState(function (state) { return (__assign(__assign({}, state), { token: null, accessToken: new Success(null) })); });
        };
        return errorHandler;
    }, [errorLogger, accessTokenState[0].accessToken.value]);
    var apiClient = React.useMemo(function () { return createApiClient(new Handler(accessTokenStorage)); }, [accessTokenStorage]);
    var pigeonHandler = React.useMemo(function () { return new PigeonHandler(accessTokenStorage, pigeonAuthProvider); }, [pigeonAuthProvider]);
    var pigeonClient = React.useMemo(function () { return new FetchClient(pigeonBaseUrl, pigeonHandler); }, [pigeonBaseUrl]);
    return (<ErrorHandler fallback={React.useCallback(function (error) { return (<ErrorAlert>{error}</ErrorAlert>); }, [])} handler={React.useCallback(function (error, errorInfo) { return errorHandler.handle(error, errorInfo !== undefined ? { react: errorInfo } : undefined); }, [errorHandler])}>
			<Router.ContextProvider>
				{function () { return (<ApiContext.Provider value={{ api: apiClient, pigeon: pigeonClient }}>
						<AccessTokenContext.Provider value={accessTokenState}>
							<FlashMessageProvider>
								<Store config={config}>
									<RootRouter />
									<FlashMessage />
								</Store>
							</FlashMessageProvider>
						</AccessTokenContext.Provider>
					</ApiContext.Provider>); }}
			</Router.ContextProvider>
		</ErrorHandler>);
};
Application.displayName = 'Application';
